import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';
import useStyles from './styles';
import GroupModal from './GroupModal';
import FilterForm from '../../../commons/FilterForm';
import Loader from '../../../commons/Loader';
import DeleteButton from '../../../commons/DeleteButton';
import GenericConfirmationModal from '../../../commons/GenericConfirmationModal';
import { groupsCustom } from '../../../../utils/custom_prop_types/customPropTypes';

const columns = [
  { id: 'number', label: 'Number', minWidth: 170 },
  { id: 'description', label: 'Description', minWidth: 170 },
];

function createData(id, number, description, products) {
  return {
    id, number, description, products,
  };
}

export default function ProductAssociationsTable({
  modals,
  setOpenModal,
  fetchProductGroupsData,
  groups,
  fetchSingleProductGroup,
  deleteContent,
}) {
  const [rows, setRows] = useState([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const classes = useStyles();

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(event.target.value);
    setPage(0);
  };

  const handleOpenModal = (row) => {
    setOpenModal('adminAssociationsGroup', true);
    fetchSingleProductGroup(row.number, row.id, row.products);
  };

  useEffect(() => {
    fetchProductGroupsData();
  }, []);

  useEffect(() => {
    const tempArray = [];
    groups.filtered?.['hydra:member'].forEach((group) => {
      tempArray.push(createData(
        group['@id'],
        group.number,
        group.products.map((product) => (
          <Chip
            label={product.description}
            variant="outlined"
            className={classes.descriptionChip}
          />
        )),
        group.products,
      ));
    });

    return setRows(tempArray);
  }, [groups]);

  return (
    <Box>
      <Box className={classes.filtersWrapper}>
        <FilterForm filterName="number" fetchBy={fetchProductGroupsData} label="Number" setPage={setPage} />
        <FilterForm filterName="products.description" fetchBy={fetchProductGroupsData} label="Description" setPage={setPage} />
        <DeleteButton destination="product groups" modal="deleteAllProductGroups" />
        {modals.deleteAllProductGroups
        && (
          <GenericConfirmationModal
            confirmationType="deleteAllProductGroups"
            confirmedFunction={() => deleteContent('productGroups')}
            informationalHeader="You are going to remove all product groups"
          />
        )}
        <DeleteButton destination="associations" modal="deleteAllAssociations" />
        {modals.deleteAllAssociations
        && (
          <GenericConfirmationModal
            confirmationType="deleteAllAssociations"
            confirmedFunction={() => deleteContent('associations')}
            informationalHeader="You are going to remove all associations"
          />
        )}
      </Box>
      <Paper className={classes.root}>
        <TableContainer className={classes.container}>
          <Table stickyHeader aria-label="sticky table" className={classes.productAssociationsTable}>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    style={{ minWidth: column.minWidth }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                <TableRow hover role="checkbox" tabIndex={-1} key={row.id} onClick={() => handleOpenModal(row)}>
                  {columns.map((column) => {
                    const value = row[column.id];
                    return (
                      <TableCell
                        key={column.id}
                        align={column.align}
                      >
                        {column.format && typeof value === 'number' ? column.format(value) : value}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Loader target="associations" size={32} centeredX centeredY />
        <Loader target="deleteContent" size={32} centeredX centeredY />
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
        {modals.adminAssociationsGroup && <GroupModal />}
      </Paper>
    </Box>
  );
}

ProductAssociationsTable.propTypes = {
  modals: PropTypes.objectOf(PropTypes.bool).isRequired,
  setOpenModal: PropTypes.func.isRequired,
  fetchProductGroupsData: PropTypes.func.isRequired,
  fetchSingleProductGroup: PropTypes.func.isRequired,
  groups: groupsCustom.isRequired,
  deleteContent: PropTypes.func.isRequired,
};
